import { formatRelative } from "@lib/format/relativeTime";
import { RESTORE_SELECTION_STEP } from "@lib/graphql/mutations";
import { type ArchivedStep, STEP_TYPE_LABELS } from "@lib/selectionSteps";
import { Archive, ChevronDown, ChevronRight, RotateCcw } from "lucide-react";
import { useState } from "react";
import { useMutation } from "urql";

interface ArchivedStepsSectionProps {
  campaignId: string;
  archived: ArchivedStep[];
  onSaved: () => void;
}

const VISIBLE_LIMIT = 10;

export function ArchivedStepsSection({ campaignId, archived, onSaved }: ArchivedStepsSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [, restoreSelectionStep] = useMutation(RESTORE_SELECTION_STEP);

  const handleRestore = async (continuityId: string) => {
    setRestoringId(continuityId);
    setRowErrors((prev) => {
      const { [continuityId]: _, ...rest } = prev;
      return rest;
    });
    try {
      const result = await restoreSelectionStep({
        input: { campaignId, continuityId },
      });
      const errors = result.data?.restoreSelectionStep?.errors as string[] | undefined;
      if (errors && errors.length > 0) {
        setRowErrors((prev) => ({ ...prev, [continuityId]: errors.join("; ") }));
        return;
      }
      if (result.error) {
        setRowErrors((prev) => ({
          ...prev,
          [continuityId]: result.error?.message ?? "Restore failed",
        }));
        return;
      }
      onSaved();
    } finally {
      setRestoringId(null);
    }
  };

  const visible =
    archived.length > VISIBLE_LIMIT && !showAll ? archived.slice(0, VISIBLE_LIMIT) : archived;

  return (
    <div data-testid="archived-steps-section" className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="archived-steps-toggle"
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs font-medium text-text-secondary hover:bg-card-hover"
      >
        <span className="flex items-center gap-2">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <Archive size={12} />
          <span>Archived steps · {archived.length}</span>
        </span>
        <span className="text-text-faint">{expanded ? "Hide" : "Show"}</span>
      </button>

      {expanded && (
        <div className="border-t border-border p-3">
          {archived.length === 0 ? (
            <p data-testid="archived-steps-empty" className="text-xs text-text-faint">
              No archived steps.
            </p>
          ) : (
            <ul className="space-y-2">
              {visible.map((step) => {
                const typeLabel = step.lastStepType
                  ? (STEP_TYPE_LABELS[step.lastStepType] ?? step.lastStepType)
                  : "—";
                const displayLabel = step.lastLabel?.trim() ? step.lastLabel : typeLabel;
                const rowError = rowErrors[step.continuityId];
                const restoring = restoringId === step.continuityId;
                return (
                  <li
                    key={step.continuityId}
                    data-testid={`archived-step-${step.continuityId}`}
                    className="rounded border border-border bg-background/30 p-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded bg-card-hover px-1.5 py-0.5 text-xs text-text-faint">
                            {typeLabel}
                          </span>
                          <span className="text-sm text-text-primary">{displayLabel}</span>
                          {step.inFlightCandidateCount > 0 && (
                            <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                              {step.inFlightCandidateCount} in flight
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-text-faint">
                          Removed{" "}
                          {step.removedAt ? formatRelative(step.removedAt) : "at unknown time"}
                          {" · "}
                          {step.removedBy ?? "system"}
                        </div>
                        {step.changeReason && (
                          <div className="text-xs italic text-text-secondary">
                            {step.changeReason}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRestore(step.continuityId)}
                        disabled={restoring}
                        data-testid={`archived-step-${step.continuityId}-restore`}
                        title="Restore this step to the end of the active selection process"
                        aria-label={`Restore ${displayLabel}`}
                        className="flex flex-shrink-0 items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <RotateCcw size={12} />
                        {restoring ? "Restoring…" : "Restore"}
                      </button>
                    </div>
                    {rowError && (
                      <div
                        data-testid={`archived-step-${step.continuityId}-error`}
                        className="mt-2 rounded bg-red-50 px-2 py-1 text-xs text-red-800"
                      >
                        {rowError}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {archived.length > VISIBLE_LIMIT && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              data-testid="archived-steps-show-all"
              className="mt-2 text-xs text-accent hover:text-accent/80"
            >
              Show all {archived.length}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
